import React from 'react';
import { Button, Theme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '../Typography';
import { ReactComponent as CircledCrossIcon } from '../../assets/icons/circled-cross.svg';
import { useAppLayoutContext } from './Context';
import { ActionEventType } from '../../types/Enums';

export const PROMO_BANNER_HEIGHT = 60;

interface BannerStyleProps {
  loginRoutes: boolean;
}

const useStyles = makeStyles<Theme, BannerStyleProps>((theme) => ({
  banner: {
    position: 'fixed',
    top: 0,
    left: '0px',
    backgroundColor: '#FB9D0E',
    color: '#150036',
    width: '100%',
    height: PROMO_BANNER_HEIGHT,
    display: 'none',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 4,
    borderBottom: '1px solid gray',
    gap: theme.spacing(2),
    zIndex: 10000,
    cursor: 'pointer',

    '@media (min-width: 768px)': {
      display: 'flex',
    },
  },

  bannerBackground: {
    display: 'none',
    width: '100%',
    height: PROMO_BANNER_HEIGHT,

    '@media (min-width: 768px)': {
      display: 'flex',
    },
  },

  openAppButton: {
    color: '#150036',
    minWidth: 'fit-content',
    backgroundColor: 'white',
    '&:focus': {
      backgroundColor: '#150036',
      color: 'white',
    },

    '&:hover': {
      backgroundColor: '#150036',
      color: 'white',
    },

    '&:active': {
      backgroundColor: '#150036',
      color: 'white',
    },
  },

  imageBackground: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '10px 10px',
    borderRadius: 12,
    backgroundColor: '#FB9D0E',
  },

  buttonContainer: {
    padding: '10px 10px',
    display: 'flex',
    gap: theme.spacing(2),
  },

  icon: {
    width: 20,
    height: 20,

    '& circle': {
      fill: '#150036',
    },

    '& path': {
      stroke: '#150036',
    },
  },
}));

interface BannerProps {
  loginRoutes?: boolean;
  onClose: () => void;
  isOpen: boolean;
}

export default function PromoBanner({ loginRoutes = false, onClose, isOpen }: BannerProps): JSX.Element {
  const classes = useStyles({ loginRoutes });
  const { t } = useTranslation();
  const history = useHistory();
  const { dispatch: appStateDispatcher } = useAppLayoutContext();

  const remainClose = sessionStorage.getItem('PromoBanner');
  const onClick = () => {
    if (window.location.pathname !== '/discover') {
      history.push('/discover', { triggerPromo: 'true' });
      return;
    }
    appStateDispatcher({
      type: ActionEventType.SEARCH_AND_FILTERS,
      payload: {
        urlParams: {
          region: 'Bordeaux',
          vintage: '2024',
        },
      },
    });
  };

  if (!isOpen || remainClose) {
    return <></>;
  }
  return (
    <>
      <div className={classes.bannerBackground} />

      <div
        className={classes.banner}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            console.log('');
          }
        }}
      >
        <div className={classes.imageBackground}>
          <CircledCrossIcon
            className={classes.icon}
            onClick={(e) => {
              onClose();
              e.stopPropagation();
            }}
          />
        </div>

        <div>
          <Typography customVariant="body2" className="tw-text-[13px] tw-font-bold">
            {t('common:promoBannerText')}
          </Typography>
        </div>
        <div className={classes.buttonContainer}>
          <Button className={classes.openAppButton} size="small" variant="contained" onClick={onClick}>
            {t('common:exploreWines')}
          </Button>
        </div>
      </div>
    </>
  );
}
